import { stableRecordKey } from '../../lib/rowIdentity';
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Users, Check, Heart, Calendar, Award, Shield, Sparkles, Plus, Trash2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { MandalaCorner } from '../../components/luxury/MandalaCorner';
import { PatolaBorder } from '../../components/luxury/PatolaBorder';
import { api } from '../../App';
import { DocumentUpload } from '../../components/DocumentUpload';
import { AddressLookup } from '../../components/AddressLookup';
import { usePageContent, PreviewBanner } from '../../lib/content';
import { toast } from 'sonner';
import { FamilyMemberRow, emptyFamilyMember, validateFamilyMember, serialiseFamilyMember, isChildDob } from '../../components/membership/FamilyMemberRow';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-80px' },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
};

const BENEFITS = [
  { icon: Calendar, title: 'Exclusive Events', desc: 'Priority access to cultural celebrations and festivals' },
  { icon: Heart, title: 'Community Support', desc: 'Connect with fellow community members' },
  { icon: Award, title: 'Member Discounts', desc: 'Special pricing on tickets and merchandise' },
  { icon: Shield, title: 'Heritage Preservation', desc: 'Help preserve Gujarati culture for future generations' },
];

export default function LuxuryMembership() {
  const { t, c, isPreview } = usePageContent('membership');
  const benefits = Array.isArray(c.benefits) && c.benefits.length ? c.benefits.map((b, i) => ({ ...b, icon: BENEFITS[i % BENEFITS.length].icon })) : BENEFITS;
  const [form, setForm] = useState({ 
    full_name: '', 
    email: '', 
    phone: '', 
    address: '', 
    postcode: '', 
    address_line_1: '',
    address_line_2: '',
    city: '',
    county: '',
    dob: '',
    gender: '',
    occupation: '',
    interests: '',
    membership_type: 'standard' 
  });
  const [proofFile, setProofFile] = useState(null);
  // Pricing comes from the single authoritative admin configuration (P1.5)
  const [plans, setPlans] = useState([]);
  useEffect(() => {
    api.get('/api/membership/pricing').then(d => setPlans((d.pricing || []).map((p, i) => ({
      type: p.type, price: `£${Number(p.price).toFixed(Number.isInteger(p.price) ? 0 : 2)}`, priceNum: Number(p.price), min_age: p.min_age,
      period: p.validity_days ? p.validity_label : 'Lifetime', desc: p.description, label: p.label, popular: i === 0,
    })))).catch(() => setPlans([]));
  }, []);
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [familyMembers, setFamilyMembers] = useState([]);

  const addFamilyMember = () => {
    if (familyMembers.length >= 15) { toast.error('Maximum 15 family members'); return; }
    setFamilyMembers([...familyMembers, emptyFamilyMember()]);
  };
  const replaceFamilyMember = (idx, next) => setFamilyMembers(familyMembers.map((f, i) => (i === idx ? next : f)));
  const removeFamilyMember = (idx) => setFamilyMembers(familyMembers.filter((_, i) => i !== idx));

  // Price summary: applicant + each 14+ family member (children under 14 are free)
  const mainPlan = plans.find(p => p.type === form.membership_type);
  const adults = familyMembers.filter(f => f.dob && !isChildDob(f.dob));
  const children = familyMembers.filter(f => f.dob && isChildDob(f.dob));
  const adultTotal = adults.reduce((s, f) => s + (plans.find(p => p.type === (f.membership_type || 'standard'))?.priceNum || 0), 0);
  const grandTotal = (mainPlan?.priceNum || 0) + adultTotal;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // ID is optional at registration (P2.1) — it can be uploaded within 7 days of payment via a secure link
    if (!consent) {
      toast.error('Please tick the consent box so we can process your application');
      return;
    }

    // Validate family members: DOB-driven rule (under 14 free, may share parent's email; 14+ own email + paid)
    const adultEmails = [];
    for (let i = 0; i < familyMembers.length; i++) {
      const err = validateFamilyMember(familyMembers[i], i, form.email, adultEmails);
      if (err) { toast.error(err, { duration: 7000 }); return; }
      if (!isChildDob(familyMembers[i].dob)) adultEmails.push(familyMembers[i].email.trim().toLowerCase());
    }
    
    setLoading(true);
    try {
      const formData = new FormData();
      Object.keys(form).forEach(key => formData.append(key, form[key]));
      formData.append('origin_url', window.location.origin);
      formData.append('consent', 'true');
      if (familyMembers.length > 0) {
        formData.append('family_members', JSON.stringify(familyMembers.map(serialiseFamilyMember)));
      }
      
      if (proofFile) {
        formData.append('proof_file', proofFile);
      }
      
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/membership/apply`, {
        method: 'POST',
        body: formData
      });
      
      if (!res.ok) {
        const err = await res.json().catch(() => ({detail: 'Application failed'}));
        throw new Error(err.detail || 'Application failed');
      }
      
      const data = await res.json();
      if (data.url) window.location.href = data.url;
      else toast.success('Thank you - your membership application has been received.');
    } catch (err) {
      toast.error(err.message || 'Application failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="overflow-hidden">
      {isPreview && <PreviewBanner page="Membership" />}
      {/* Hero */}
      <section className="relative py-20 bg-gradient-to-br from-[hsl(var(--lux-royal-soft))] to-[hsl(var(--lux-ivory))] overflow-hidden">
        <MandalaCorner position="top-right" className="opacity-20" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <Badge className="mb-6 bg-[hsl(var(--lux-gold-soft))] text-[hsl(var(--lux-royal))] border-[hsl(var(--lux-gold)/0.4)]">
              <Sparkles className="w-3 h-3 mr-1" /> {t('hero_badge', 'Join Our Community')}
            </Badge>
            <h1 className="text-5xl sm:text-6xl font-bold text-[hsl(var(--lux-royal))] mb-6" style={{ fontFamily: 'Cormorant Garamond' }} data-testid="luxury-membership-title">
              {t('hero_title', 'Become a')} <span className="text-[hsl(var(--lux-gold))]">{t('hero_title_accent', 'Member')}</span>
            </h1>
            <p className="text-lg text-[hsl(var(--lux-ink)/0.8)] max-w-2xl mx-auto mb-8">
              {t('hero_subtitle', 'Join Gujarat Samaj Nottingham and be part of a vibrant community celebrating our rich cultural heritage.')}
            </p>
            <div className="lux-gold-divider mx-auto max-w-md" />
            <div className="mt-8 inline-flex flex-col sm:flex-row items-center gap-3 rounded-xl border border-[hsl(var(--lux-gold)/0.5)] bg-white/70 px-5 py-3 text-sm text-[hsl(var(--lux-ink)/0.85)]" data-testid="returning-member-prompt">
              <span>{t('returning_prompt', 'Do you already have a GS Membership Number?')}</span>
              <Link to="/membership/returning" className="font-semibold text-[hsl(var(--lux-royal))] underline underline-offset-4 hover:text-[hsl(var(--lux-gold))]" data-testid="returning-member-link">{t('returning_link_label', 'Yes — upload my ID document')}</Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits Grid */}
      <section className="py-16 bg-[hsl(var(--lux-parchment))]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h2 {...fadeInUp} className="text-3xl font-bold text-center text-[hsl(var(--lux-royal))] mb-12" style={{ fontFamily: 'Cormorant Garamond' }}>
            Membership Benefits
          </motion.h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, idx) => (
              <motion.div key={stableRecordKey(benefit)} {...fadeInUp} transition={{ delay: idx * 0.1 }}>
                <Card className="lux-ornate-card h-full text-center hover:shadow-[var(--lux-shadow-2)] transition-all">
                  <CardContent className="p-6">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[hsl(var(--lux-royal-soft))] flex items-center justify-center">
                      <benefit.icon className="w-8 h-8 text-[hsl(var(--lux-royal))]" />
                    </div>
                    <h3 className="text-lg font-bold text-[hsl(var(--lux-royal))] mb-2" style={{ fontFamily: 'Cormorant Garamond' }}>
                      {benefit.title}
                    </h3>
                    <p className="text-sm text-[hsl(var(--lux-ink)/0.7)]">{benefit.desc}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-6">
            {plans.map((plan) => (
              <motion.div key={plan.type} {...fadeInUp}>
                <Card className={`lux-ornate-card h-full ${plan.popular ? 'border-2 border-[hsl(var(--lux-gold))]' : ''} ${form.membership_type === plan.type ? 'ring-2 ring-[hsl(var(--lux-royal))]' : ''}`}>
                  {plan.popular && <PatolaBorder />}
                  <CardContent className="p-8">
                    {plan.popular && <Badge className="mb-4 bg-[hsl(var(--lux-gold))] text-white">Most Popular</Badge>}
                    <h3 className="text-2xl font-bold text-[hsl(var(--lux-royal))] mb-2" style={{ fontFamily: 'Cormorant Garamond' }} data-testid={`luxury-membership-plan-${plan.type}`}>
                      {plan.label} Membership
                    </h3>
                    <p className="text-sm text-[hsl(var(--lux-ink)/0.6)] mb-6">{plan.desc}</p>
                    <div className="mb-6">
                      <span className="text-5xl font-bold text-[hsl(var(--lux-gold))]" style={{ fontFamily: 'Cormorant Garamond' }}>{plan.price}</span>
                      <span className="text-[hsl(var(--lux-ink)/0.6)] ml-2">/ {plan.period}</span>
                    </div>
                    <ul className="space-y-3 mb-8">
                      {['Member discounts', 'Priority booking', 'Exclusive invites', 'Community access'].map((item, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm">
                          <Check className="w-4 h-4 text-[hsl(var(--lux-gold))]" />
                          {item}
                        </li>
                      ))}
                    </ul>
                    <Button
                      className={`w-full ${form.membership_type === plan.type ? 'bg-[hsl(var(--lux-royal-2))]' : 'bg-[hsl(var(--lux-royal))]'} text-white hover:bg-[hsl(var(--lux-royal-2))]`}
                      onClick={() => {
                        setForm({...form, membership_type: plan.type});
                      }}
                      data-testid={`luxury-membership-select-${plan.type}`}
                    >
                      <Users className="w-4 h-4 mr-2" />
                      {form.membership_type === plan.type ? 'Selected' : `Choose ${plan.label}`}
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section className="py-16 bg-[hsl(var(--lux-parchment))]">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeInUp}>
            <Card className="lux-ornate-card">
              <CardContent className="p-8">
                <h2 className="text-3xl font-bold text-[hsl(var(--lux-royal))] mb-6 text-center" style={{ fontFamily: 'Cormorant Garamond' }}>
                  Membership Application
                </h2>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <Label>Full Name *</Label>
                    <Input required value={form.full_name} onChange={e => setForm({...form, full_name: e.target.value})} data-testid="luxury-membership-name-input" />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label>Email *</Label>
                      <Input type="email" required value={form.email} onChange={e => setForm({...form, email: e.target.value})} data-testid="luxury-membership-email-input" />
                    </div>
                    <div>
                      <Label>Phone *</Label>
                      <Input type="tel" required value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} data-testid="luxury-membership-phone-input" />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label>Date of Birth *</Label>
                      <Input 
                        type="date" 
                        required 
                        value={form.dob} 
                        onChange={e => setForm({...form, dob: e.target.value})} 
                        data-testid="luxury-membership-dob-input"
                        max={new Date().toISOString().split('T')[0]}
                      />
                    </div>
                    <div>
                      <Label>Gender *</Label>
                      <select
                        required
                        value={form.gender || ''}
                        onChange={e => setForm({...form, gender: e.target.value})}
                        className="w-full h-10 rounded-lg border border-[hsl(var(--input))] px-3 text-sm bg-white"
                        data-testid="luxury-membership-gender-select"
                      >
                        <option value="">Select gender</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                        <option value="other">Other / prefer not to say</option>
                      </select>
                    </div>
                  </div>
                  <AddressLookup
                    address={form.address}
                    postcode={form.postcode}
                    address_line_1={form.address_line_1}
                    address_line_2={form.address_line_2}
                    city={form.city}
                    county={form.county}
                    onChange={({ address, postcode, address_line_1, address_line_2, city, county }) => setForm(f => ({ ...f, address, postcode, address_line_1, address_line_2, city, county }))}
                    testIdPrefix="luxury-membership"
                  />
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label>Occupation <span className="text-xs text-[hsl(var(--lux-ink)/0.5)]">(optional)</span></Label>
                      <Input value={form.occupation} onChange={e => setForm({...form, occupation: e.target.value})} placeholder="e.g. Teacher, Engineer" data-testid="luxury-membership-occupation-input" />
                    </div>
                    <div>
                      <Label>Interests <span className="text-xs text-[hsl(var(--lux-ink)/0.5)]">(optional)</span></Label>
                      <Input value={form.interests} onChange={e => setForm({...form, interests: e.target.value})} placeholder="e.g. Garba, Cooking, Cricket" data-testid="luxury-membership-interests-input" />
                    </div>
                  </div>
                  
                  {/* Family Members */}
                  <div className="p-4 bg-white rounded-lg border border-[hsl(var(--lux-gold)/0.3)] min-w-0" data-testid="family-members-section">
                    <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                      <Label className="text-[hsl(var(--lux-royal))] font-semibold flex items-center gap-2">
                        <Users className="w-4 h-4" /> Family Members <span className="text-xs font-normal text-[hsl(var(--lux-ink)/0.5)]">(optional)</span>
                      </Label>
                      <Button type="button" variant="outline" size="sm" onClick={addFamilyMember} className="border-[hsl(var(--lux-gold)/0.5)] text-[hsl(var(--lux-royal))]" data-testid="add-family-member-button">
                        <Plus className="w-4 h-4 mr-1" /> Add family member
                      </Button>
                    </div>
                    <p className="text-xs text-[hsl(var(--lux-ink)/0.6)] mb-3" data-testid="family-members-rule">
                      You are welcome to include your family. Each person receives their <strong>own membership card</strong>.
                      Children under 14 are <strong>complimentary</strong> and may be reached through your email address. Family members aged 14 and over
                      join as members in their own right, with their own email address and membership.
                    </p>
                    {familyMembers.map((fm, idx) => (
                      <FamilyMemberRow key={stableRecordKey(fm)} fm={fm} idx={idx} plans={plans} parentEmail={form.email}
                        onChange={(next) => replaceFamilyMember(idx, next)} onRemove={() => removeFamilyMember(idx)} />
                    ))}
                    <p className="text-xs text-[hsl(var(--lux-ink)/0.55)] mt-1">Already a member? You can <a href="/membership/renew" className="text-[hsl(var(--lux-royal))] underline font-medium" data-testid="renew-link">renew your membership</a> or <a href="/membership/add-family" className="text-[hsl(var(--lux-royal))] underline font-medium" data-testid="add-family-later-link">add a family member</a> at any time.</p>
                  </div>

                  {/* Payment summary */}
                  {mainPlan && (
                    <div className="rounded-lg border border-[hsl(var(--lux-gold)/0.4)] bg-white p-4 text-sm space-y-1" data-testid="membership-price-summary">
                      <div className="flex justify-between"><span>Your {mainPlan.label} membership</span><span className="tabular-nums">{mainPlan.price}</span></div>
                      {adults.map((f, i) => { const pl = plans.find(p => p.type === (f.membership_type || 'standard')); return (
                        <div key={stableRecordKey(f)} className="flex justify-between"><span>{f.name.trim() || 'Family member'} · {pl?.label || 'Standard'} membership</span><span className="tabular-nums">{pl?.price || '—'}</span></div>
                      ); })}
                      {children.map((f, i) => (
                        <div key={stableRecordKey(f)} className="flex justify-between text-emerald-800"><span>{f.name.trim() || 'Child'} · Child membership</span><span>Complimentary</span></div>
                      ))}
                      <div className="flex justify-between font-semibold border-t pt-2 mt-2 text-[hsl(var(--lux-royal))]"><span>Total to pay today</span><span className="tabular-nums" data-testid="membership-total">£{grandTotal.toFixed(2)}</span></div>
                    </div>
                  )}

                  {/* Proof of ID — OPTIONAL now; may be uploaded within 7 days of payment (P2.1) */}
                  <div className="p-4 bg-[hsl(var(--lux-royal-soft))] rounded-lg border border-[hsl(var(--lux-gold)/0.3)]" data-testid="luxury-membership-id-section">
                    <Label className="text-[hsl(var(--lux-royal))] font-semibold mb-2">
                      Proof of ID (optional now) {form.membership_type === 'senior_65plus' ? '— also serves as proof of age for 65+' : ''}
                    </Label>
                    <p className="text-sm text-[hsl(var(--lux-ink)/0.7)] mb-3">
                      Upload a clear photo or scan of a valid ID showing your full name and date of birth (Passport, Driving Licence or Birth Certificate) now,
                      <strong> or within 7 calendar days of payment</strong> using the secure link we email you. Your membership number is issued as soon as payment
                      completes; your membership stays <em>provisional</em> (no member benefits or card) until the ID is verified. Stored securely, seen only by authorised staff.
                    </p>
                    <DocumentUpload
                      files={proofFile ? [proofFile] : []}
                      onChange={(fs) => setProofFile(fs[0] || null)}
                      maxFiles={1}
                      maxSizeMb={30}
                      accept="documents+images"
                      label=""
                      testId="luxury-membership-proof-upload"
                    />
                  </div>
                  
                  {/* GDPR consent */}
                  <label className="flex items-start gap-3 p-4 rounded-xl border border-[hsl(var(--lux-gold)/0.4)] bg-[hsl(var(--lux-royal-soft))/0.4] cursor-pointer" data-testid="membership-consent-label">
                    <input
                      type="checkbox"
                      checked={consent}
                      onChange={e => setConsent(e.target.checked)}
                      className="mt-1 w-4 h-4 accent-[hsl(var(--lux-royal))]"
                      data-testid="membership-consent-checkbox"
                    />
                    <span className="text-sm text-[hsl(var(--lux-ink)/0.85)]">
                      I consent to Gujarat Samaj Nottingham processing my personal data (including my date of birth
                      and, for Age 65+ membership, my proof of age) to administer my membership, in line with the{' '}
                      <a href="/privacy-policy" target="_blank" rel="noreferrer" className="text-[hsl(var(--lux-royal))] underline font-medium">Privacy Policy</a>.
                      I can withdraw consent or request my data at any time. <span className="text-[hsl(var(--lux-vermilion))]">*</span>
                    </span>
                  </label>

                  <Button type="submit" disabled={loading} className="w-full bg-[hsl(var(--lux-royal))] text-white hover:bg-[hsl(var(--lux-royal-2))] h-12 text-lg" data-testid="luxury-membership-submit-button">
                    {loading ? 'Processing...' : `Join as ${form.membership_type === 'senior_65plus' ? 'Age 65+' : form.membership_type} Member${familyMembers.length ? ` · pay £${grandTotal.toFixed(2)}` : ''}`}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
